import React, {useEffect, useState} from 'react';
import {
  Box,
  Button,
  Grid,
  MUIDataTable,
  MUIDataTableColumnDef,
  Pill,
  SearchBar,
  TableActionTypes,
  useDataTable,
} from 'web-library';
import {TFunction, useTranslation} from 'react-i18next';


import {makeStyles} from 'tss-react/mui';
import {useParams} from 'react-router-dom';
import FormHeader from '../../../components/form-items/FormHeader';
import {useUniqueLink} from './logic/useUniqueLink';
import GrantAccessModal from './GrantAccessModal';

import {GenericExamLevel} from '../../../api/candidate/exam/model';
import {
  CandidateUniqueLinkCountResponse,
  usePostCandidateLinkSearchNoPagination,
} from '../../../api-refactored/unique-link/useGetCandidateUniqueLinkCount';
import UniqueLinkListingModal from './UniqueLinkListingModal';

const useStyles = makeStyles()((theme) =>
    ({
      tableContainer: {
        marginTop: theme.spacing(3),
      },
      viewDetailsButton: {
        padding: 0,
        minHeight: 0,
        minWidth: 0,
        whiteSpace: 'nowrap',
        height: theme.spacing(3),
      },
    }));

const displayManageUniqueLinkButton = (
    candidateUuid: string,
    uniqueLinkCount: number,
    t: TFunction,
    classes: Record<string, string>,
    setSelectedCandidateUuid: React.Dispatch<React.SetStateAction<string | undefined>>,
    isGCESelected: boolean
) =>
    uniqueLinkCount > 0 &&
    isGCESelected && (
        <>
          <Button
              className={classes.viewDetailsButton}
              color="primary"
              variant="text"
              size="small"
              onClick={() => {
                setSelectedCandidateUuid(candidateUuid);
              }}
          >
            {t('admin->>UniqueLink.ManageUniqueLink')}
          </Button>
        </>
    );

/** define Mui DataTable Column Definition
 * */
const tableColumns = (
    classes: any,
    t: TFunction,
    candidateList: CandidateUniqueLinkCountResponse[],
    setEditDetail: (name: string, uuid: string, index: number) => void,
    sendEmailStatusList: string[],
    hasCreatePermission: boolean,
    setSelectedCandidateUuid: React.Dispatch<React.SetStateAction<string | undefined>>,
    isGCESelected: boolean
): MUIDataTableColumnDef[] => [
  {
    name: 'uinFinPassport',
    label: t('admin->>UniqueLink.UinFinOrPassword'),
    options: {
      sort: false,
      searchable: true,
    },
  },
  {
    name: 'candidateName',
    label: t('admin->>UniqueLink.CandidateName'),
    options: {
      sort: false,
      searchable: true,
    },
  },
  {
    name: 'email',
    label: t('admin->>UniqueLink.Email'),
    options: {
      sort: false,
      searchable: true,
    },
  },
  {
    name: 'school',
    label: t('admin->>UniqueLink.School'),
    options: {
      sort: false,
      searchable: true,
    },
  },
  {
    name: 'action',
    label: t('admin->>UniqueLink.Action'),
    options: {
      customBodyRenderLite: (dataIndex: number) =>
          !sendEmailStatusList[dataIndex] || sendEmailStatusList[dataIndex] !== 'success' ? (
              <>
                <Button
                    className={classes.viewDetailsButton}
                    color="primary"
                    variant="text"
                    size="small"
                    onClick={() => {
                      setEditDetail(
                          candidateList[dataIndex].candidateName,
                          candidateList[dataIndex].uuid,
                          dataIndex
                      );
                    }}
                >
                  {t('admin->>UniqueLink.GrantAccess')}
                </Button>
                {displayManageUniqueLinkButton(
                    candidateList[dataIndex].uuid,
                    candidateList[dataIndex].uniqueLinkCount,
                    t,
                    classes,
                    setSelectedCandidateUuid,
                    isGCESelected
                )}
              </>
          ) : (
              <>
                <Pill label={t('admin->>UniqueLink.EmailSent')} size="S" variant="positive"/>
                {displayManageUniqueLinkButton(
                    candidateList[dataIndex].uuid,
                    candidateList[dataIndex].uniqueLinkCount,
                    t,
                    classes,
                    setSelectedCandidateUuid,
                    isGCESelected
                )}
              </>
          ),
      sort: false,
      filter: false,
      display: `${hasCreatePermission}`,
    },
  },
];

const CandidateExamRegistrationList = ({
                                         selectedExamType,
                                         setExamLevelError,
                                         hasCreatePermission,
                                         isGCESelected,
                                         selectedExamLevel,
                                       }: {
  selectedExamType?: string;
  setExamLevelError: (examLevelError: boolean) => void;
  hasCreatePermission: boolean;
  isGCESelected: boolean;
  selectedExamLevel?: GenericExamLevel;
}) => {
  const {t} = useTranslation();
  const [helperText, setHelperText] = useState<string>();
  const [searchError, setSearchError] = useState<boolean>(false);
  const [selectedCandidateUuid, setSelectedCandidateUuid] = useState<string>();

  const {
    grantAccessModalOpen,
    selectedUserInfo,
    sendEmailStatusList,
    setSentEmailStatusList,
    apiEmailStatusCallback,
    onGrantAccessDialogClose,
    setEditDetail,
  } = useUniqueLink();

  const {classes} = useStyles();
  const {state, dispatch, tOptions} = useDataTable();
  const {nationalExamType} = useParams<{ nationalExamType: 'national' | 'non-national' }>();

  const {mutate, isLoading} = usePostCandidateLinkSearchNoPagination({
    onSuccess: (data) => {
      dispatch({
        type: 'UPDATE_DATA',
        payload: {data: data.content, count: data.totalElements},
      });
    },
  });

  const fetchCandidate = () => {
    mutate({
      page: state.page,
      size: state.size,
      body: {q: state.search},
    });
  };

  const resetDataTable = () => {
    dispatch({
      type: 'UPDATE_DATA',
      payload: {
        data: [],
        count: 0,
      },
    } as TableActionTypes);

    dispatch({
      type: 'UPDATE_SEARCH',
      payload: '',
    });
  };

  /** handle candidate search
   * */
  const onCandidateSearch = (candidateFilter: string) => {
    dispatch({
      type: 'UPDATE_SEARCH',
      payload: candidateFilter.trim(),
    });
    setHelperText(undefined);
    setSearchError(false);
    setSentEmailStatusList([]);
  };

  useEffect(() => {
    if (!state.search) return;

    mutate({
      page: state.page,
      size: state.size,
      body: {q: state.search},
    });
  }, [mutate, state.page, state.size, state.search]);

  return (
      <>
        <Grid container direction="column">
          <FormHeader title={t('admin->>UniqueLink.CandidateDetails')}/>

          <Box>
            <SearchBar
                name="searchInput"
                ariaLabel="searchInput"
                defaultValue=""
                placeholder={t('admin->>UniqueLink.SearchNameUinFinOrPassword')}
                helperText={helperText}
                onSearch={(str) => {
                  if (selectedExamType === '' || selectedExamType === undefined) {
                    setExamLevelError(true);
                    resetDataTable();
                    return;
                  }
                  if (str === '') {
                    setHelperText(t('admin->>UniqueLink.SearchErrorNameUinFinOrPassword'));
                    setSearchError(true);
                    resetDataTable();
                    return;
                  }
                  if (str.length < 3) {
                    setHelperText(
                        t('admin->>UniqueLink.SearchErrorMinCharacters', {minSearchLength: 3})
                    );
                    setSearchError(true);
                    resetDataTable();
                    return;
                  }
                  onCandidateSearch(str);
                }}
                error={searchError}
            />
          </Box>
          {grantAccessModalOpen && selectedUserInfo && selectedExamType && (
              <GrantAccessModal
                  accountType="CANDIDATES"
                  examLevelUuid={selectedExamType}
                  userInfo={selectedUserInfo}
                  modalState={grantAccessModalOpen}
                  onDialogClose={onGrantAccessDialogClose}
                  updateEmailStatus={apiEmailStatusCallback}
                  showWavieSubjectFee={nationalExamType === 'national'}
                  applicationType={nationalExamType === 'national' ? 'EXAM' : 'NON_NATIONAL_EXAM'}
                  isGCESelected={isGCESelected}
                  fetchCandidate={fetchCandidate}
              />
          )}

          <UniqueLinkListingModal
              selectedCoordinatorUuid={selectedCandidateUuid}
              setSelectedCoordinatorUuid={setSelectedCandidateUuid}
              examLevel={isGCESelected ? GenericExamLevel.GCE : undefined}
              userType={'Candidate'}
          />
        </Grid>
        <div className={classes.tableContainer}>
          <MUIDataTable
              key="candidate"
              isLoading={isLoading}
              columns={tableColumns(
                  classes,
                  t,
                  state.data,
                  setEditDetail,
                  sendEmailStatusList,
                  hasCreatePermission,
                  setSelectedCandidateUuid,
                  isGCESelected
              )}
              data={state.data}
              dispatch={dispatch}
              state={state}
              options={{
                ...tOptions,
                filter: false,
                sort: false,
                pagination: true,
              }}
              rowsPerPageOptions={[5, 10, 20, 25]}
          />
        </div>
      </>
  );
};

export default CandidateExamRegistrationList;
